import React, { useState } from 'react';
import moment from 'moment';
import {
    Select,
    DateInput,
    Input,
    OnOffSwitch,
    SecondaryButton,
    CircularLoading,
    SearchableDropdown,
    RecentLeaks,
} from '../../Common';
import Enums from '../../../enums';
import {
    Box,
    Grid,
    Collapse,
    Table,
    TableHead,
    TableBody,
    TableCell,
    TableRow,
    Card,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InformationAlert from '../../Common/InformationAlert';
import ApplianceScanLookup from './ApplianceScanLookup';
import { DetailPanelMUI, DetailPanelMUIData } from '../../Common/DetailPanel';
import { menuProps, getContainerSpacing, EquipmentOption, TechnicianOption } from '../utils';
import { renderWeight } from '../../../Utils/weight_utils';
import { TranslateW } from "../../../Utils/Languages/Translate";
import { trakrefFontFamily, isMobile } from '../../../utils';
import { Grey1, Grey2 } from '../../../color_utils';

const EnterService = ({
    loading,
    dropdownOptions,
    changeServiceType = () => { },
    formState,
    setGlobalState,
    requiredFieldsMissing,
    fieldsWithErrors: {
        serviceDate: {
            errorExists = false,
            helperText = "",
        } = {}
    },
    warning,
    setWarning,
    isEditing,
    handleAssetSelect,
    shouldFetchCylinders,
    fetchCylindersForAsset,
    renderServiceActionField,
    handleOpenVitalStats,
    vitalStatData,
    recentLeaksData = { leaks: [], totalCount: 0, loading: false },
}) => {
    const vitalStatsTableStyle = {
        fontFamily: trakrefFontFamily,
        color: Grey2,
        fontSize: '.813rem',
    };
    const serviceEventType = formState.workItemTypeId;
    const [assetSearchValue, setAssetSearchValue] = useState('');
    const renderAssetDetails = !isMobile && formState.assetId;
    const renderStatusAfterService = (
        serviceEventType === Enums.SE.WorkItemType.RecordRefrigerant
    );
    const renderNewLocation = serviceEventType === Enums.SE.WorkItemType.Move;
    const dynamicWidth = (
        renderServiceActionField
        || renderStatusAfterService
        || renderNewLocation
    ) ? 6 : 12;
    const renderR3Filed = (
        serviceEventType === Enums.SE.WorkItemType.ServiceOrLeakRepair
        || serviceEventType === Enums.SE.WorkItemType.Retrofit
        || serviceEventType === Enums.SE.WorkItemType.AirFilterChangeOut
        || serviceEventType === Enums.SE.WorkItemType.ShutdownMothball
        || serviceEventType === Enums.SE.WorkItemType.Install
        || serviceEventType === Enums.SE.WorkItemType.Reinstall
        || serviceEventType === Enums.SE.WorkItemType.MaintenanceNoGas
        || serviceEventType === Enums.SE.WorkItemType.RecordRefrigerant
        || serviceEventType === Enums.SE.WorkItemType.Move
        || serviceEventType === Enums.SE.WorkItemType.RecordVitalStats
    );
    const renderWorkItemNotes = (
        serviceEventType === Enums.SE.WorkItemType.ShutdownMothball
        || serviceEventType === Enums.SE.WorkItemType.Retrofit
        || serviceEventType === Enums.SE.WorkItemType.Install
        || serviceEventType === Enums.SE.WorkItemType.Reinstall
    );
    const renderPartsRequired = (
        serviceEventType === Enums.SE.WorkItemType.Retrofit
        || serviceEventType === Enums.SE.WorkItemType.Install
        || serviceEventType === Enums.SE.WorkItemType.Reinstall
    );
    const renderLeakCalibration = (
        serviceEventType === Enums.SE.WorkItemType.LeakMonitorCalibration
    );

    const renderEnterSeparateTicket = [
        Enums.SE.WorkItemType.LeakInspection,
        Enums.SE.WorkItemType.LeakInspection2
    ].includes(serviceEventType);

    const handleTechnicianSelect = (selectedTechID) => {
        setGlobalState("primaryTechnicianId", selectedTechID);
        if (!selectedTechID) return;
        const { leakDetectionMethodID: selectedTechLeakDetectionMethod } = dropdownOptions.technicians.find(tech => tech.id === selectedTechID);
        // set Leak Detection Methods to that of the selected technician
        if (!!selectedTechLeakDetectionMethod) {
            setGlobalState("initialLeakInspection.leakDetectionMethodId", selectedTechLeakDetectionMethod, true);
            setGlobalState("initialVerificationLeakInspection.leakDetectionMethodId", selectedTechLeakDetectionMethod, true);
            setGlobalState("verificationLeakInspection.leakDetectionMethodId", selectedTechLeakDetectionMethod, true);
        }
        if (shouldFetchCylinders(serviceEventType)) {
            fetchCylindersForAsset(selectedTechID, formState.assetId);
        }
    };

    const AssetDetailsTable = ({ asset = {} }) => (
        <DetailPanelMUI>
            <DetailPanelMUIData title={TranslateW("settingsVitalStatsCapture.system-name")} value={asset.name} />
            <DetailPanelMUIData title={TranslateW("AssetHistoryList.material-type")} value={asset.materialTypeName} />
            <DetailPanelMUIData title={TranslateW("configurableVitalStats.asset-type")} value={asset.assetTypeName} />
            <DetailPanelMUIData title={TranslateW("rapidCylinderEntry.gas-capacity")} value={renderWeight(asset.gasChargeCapacityLbs)} />
            <DetailPanelMUIData title={TranslateW("assetEditView.asset-status")} value={asset.coolingApplianceStatusName} />
            <DetailPanelMUIData title={TranslateW("serviceEventView.assetDetailInfo.subsystem-id")} value={formState.subsystemId} />
            <DetailPanelMUIData
                title={TranslateW("InstallDateHeader")}
                value={asset.installDate ? moment(asset.installDate).format("L") : ""}
            />
            <DetailPanelMUIData
                title={TranslateW("shutdownDate")}
                value={asset.disbursementDate ? moment(asset.disbursementDate).format("L") : ""}
            />
            <Grid
                item
                xs={4}
                md={4}
                lg={4}
                sx={{ display: { xs: 'none' } }}
            >
                <SecondaryButton
                    useSmallButton
                    buttonText='Record Vital Stat'
                    onClick={handleOpenVitalStats}
                />
            </Grid>
        </DetailPanelMUI>
    );

    const VitalStatTable = () => (
        <Card variant='outlined'>
            {vitalStatData.loading ? (
                <CircularLoading />
            ) : (
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            {vitalStatData.tableHeaders.map((header, index) => (
                                <TableCell key={index} sx={vitalStatsTableStyle}>
                                    {header}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {vitalStatData.tableValues.map((
                            {
                                statName,
                                statValue,
                                collectionDate,
                                technicianFullName,
                                notes
                            },
                            index
                        ) => (
                            <TableRow
                                key={index}
                                sx={{
                                    '&:last-child td, &:last-child th': { border: 0 },
                                    backgroundColor: index % 2 === 0 ? "rgba(216,216,216,0.15)" : "#FFF"
                                }}
                            >
                                <TableCell scope="row" sx={vitalStatsTableStyle}>
                                    {statName}
                                </TableCell>
                                <TableCell scope="row" sx={vitalStatsTableStyle}>
                                    {statValue}
                                </TableCell>
                                <TableCell scope="row" sx={vitalStatsTableStyle}>
                                    {moment(collectionDate).format('l LT')}
                                </TableCell>
                                <TableCell scope="row" sx={vitalStatsTableStyle}>
                                    {technicianFullName}
                                </TableCell>
                                <TableCell scope="row" sx={vitalStatsTableStyle}>
                                    {notes}
                                </TableCell>
                            </TableRow>
                        ))
                        }
                    </TableBody>
                </Table>
            )}
        </Card>
    );

    return (
        <Grid container spacing={getContainerSpacing()}>
            <Grid item xs={12} md={6}>
                <SearchableDropdown
                    required
                    label={TranslateW("EquipmentWorkedOn")}
                    value={formState.assetId}
                    onChange={(id) => { handleAssetSelect({ assetId: id }); setAssetSearchValue(''); }}
                    options={dropdownOptions.assets}
                    error={requiredFieldsMissing.assetId}
                    disabled={loading || isEditing}
                    searchFields={['name', 'serialNumber', 'tagNumber']}
                    getOptionLabel={(option) => option.label}
                    renderOption={EquipmentOption}
                    groupBy={(option) => option.assetStatus}
                    groupOrder={['active', 'inactive', 'decommissioned']}
                    emptyStateText={TranslateW('NoMatchingEquipmentFound')}
                    inputValue={assetSearchValue}
                    onInputChange={setAssetSearchValue}
                    endAdornmentPrefix={
                        <ApplianceScanLookup
                            assets={dropdownOptions.assets}
                            onSelect={(assetId) => handleAssetSelect({ assetId })}
                            onSearchValue={setAssetSearchValue}
                            serviceEventType={serviceEventType}
                            disabled={loading || isEditing}
                        />
                    }
                />
            </Grid>
            {formState.assetId &&
                formState.workItemTypeId !== Enums.SE.WorkItemType.Install &&
                dropdownOptions.assets.find(a => a.id === formState.assetId)?.subsystems?.length > 0 && (
                    <Grid item xs={12} md={6}>
                        <Select
                            label={TranslateW("serviceEventView.showSubsystemDropdown.subsystem-worked-on")}
                            value={formState.subsystemId}
                            onChange={e => setGlobalState("subsystemId", e.target.value)}
                            options={dropdownOptions.assets.find(a => a.id === formState.assetId)?.subsystems || []}
                            menuProps={menuProps}
                            disabled={loading || formState.workItemStatusId === Enums.WorkItemStatusTypes.Closed}
                        />
                    </Grid>
            )}
            {isMobile && formState.assetId && (
                <Grid item xs={12} md={12}>
                    <AssetDetailsTable asset={dropdownOptions.assets.find(a => a.id === formState.assetId)} />
                </Grid>
            )}
            <Grid item xs={12} md={6}>
                <Select
                    required
                    label={TranslateW("TypeOfService")}
                    value={formState.workItemTypeId}
                    onChange={e => changeServiceType(e.target.value)}
                    options={dropdownOptions.workItemTypes}
                    disabled={!formState.assetId || isEditing}
                />
            </Grid>
            {renderAssetDetails && (
                <>
                    <Grid item xs={12} md={12}>
                        <AssetDetailsTable asset={dropdownOptions.assets.find(a => a.id === formState.assetId)} />
                    </Grid>
                    {vitalStatData.tableValues && vitalStatData.tableValues.length > 0 && (
                        <Grid item xs={12} md={12}>
                            <Accordion
                                expanded={formState.vitalStatsOpen}
                                onChange={() => setGlobalState("vitalStatsOpen", !formState.vitalStatsOpen)}
                                variant="outlined"
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography
                                        component="span"
                                        sx={{ fontFamily: trakrefFontFamily, fontSize: "1.35rem", color: Grey1 }}
                                    >
                                        Vital Stats
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <VitalStatTable />
                                </AccordionDetails>
                            </Accordion>
                        </Grid>
                    )}
                </>
            )}
            {renderEnterSeparateTicket && warning && (
                <Grid item xs={12} md={12}>
                    <Collapse in={warning}>
                        <Grid item xs={12} md={12}>
                            <InformationAlert
                                title={TranslateW("Information")}
                                message={TranslateW("EnterSeparateTicket")}
                                onClose={() => setWarning(false)}
                            />
                        </Grid>
                    </Collapse>
                </Grid>
            )}
            <Grid item xs={12} md={6}>
                <SearchableDropdown
                    required
                    label={TranslateW("serviceEventView.primary-technician")}
                    value={formState.primaryTechnicianId}
                    onChange={(id) => handleTechnicianSelect(id)}
                    options={dropdownOptions.technicians}
                    error={requiredFieldsMissing.primaryTechnicianId}
                    disabled={!formState.workItemTypeId}
                    searchFields={['lastName', 'firstName']}
                    getOptionLabel={(option) => option.label}
                    renderOption={TechnicianOption}
                    emptyStateText={'No matching technician found'}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <SearchableDropdown
                    label={TranslateW("serviceEventView.secondary-technician")}
                    value={formState.secondaryTechnicianId}
                    onChange={(id) => setGlobalState("secondaryTechnicianId", id)}
                    options={dropdownOptions.technicians}
                    disabled={!formState.workItemTypeId}
                    searchFields={['lastName', 'firstName']}
                    getOptionLabel={(option) => option.label}
                    renderOption={TechnicianOption}
                    emptyStateText={'No matching technician found'}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <DateInput
                    showTodayButton
                    allowPast
                    allowFuture={false}
                    date={formState.serviceDate}
                    onChange={new_date => setGlobalState("serviceDate", new_date)}
                    label={`${TranslateW("serviceEventView.service-date")}*`}
                    error={requiredFieldsMissing.serviceDate || errorExists}
                    customHelperText={helperText}
                    disabled={!formState.workItemTypeId}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <Input
                    fullWidth
                    label={TranslateW('serviceEventView.reference-number')}
                    value={formState.referenceNumber}
                    onChange={e => setGlobalState("referenceNumber", e.target.value)}
                    error={requiredFieldsMissing.referenceNumber}
                    disabled={!formState.workItemTypeId}
                />
            </Grid>
            {renderLeakCalibration && (
                <>
                    <Grid item xs={12} md={6}>
                        <Input
                            fullWidth
                            required
                            label={TranslateW('serviceEventView.alarm-set-point')}
                            value={formState.alarmSetPoint}
                            onChange={e => setGlobalState("alarmSetPoint", e.target.value)}
                            error={requiredFieldsMissing.alarmSetPoint}
                            disabled={!formState.workItemTypeId}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Input
                            fullWidth
                            required
                            label={TranslateW('serviceEventView.detection-limit')}
                            value={formState.detectionLimit}
                            onChange={e => setGlobalState("detectionLimit", e.target.value)}
                            error={requiredFieldsMissing.detectionLimit}
                            disabled={!formState.workItemTypeId}
                        />
                    </Grid>
                </>
            )}
            {renderPartsRequired && (
                <Grid item xs={12} md={12} lg={12}>
                    <Input
                        fullWidth
                        multiLine
                        label={TranslateW("generalService.parts-required")}
                        value={formState.partsRequired}
                        onChange={e => setGlobalState("partsRequired", e.target.value)}
                        disabled={!formState.workItemTypeId}
                    />
                </Grid>
            )}
            {renderWorkItemNotes && (
                <Grid item xs={12} md={12} lg={12}>
                    <Input
                        required={(
                            serviceEventType === Enums.SE.WorkItemType.Install ||
                            serviceEventType === Enums.SE.WorkItemType.Reinstall ||
                            serviceEventType === Enums.SE.WorkItemType.Retrofit
                        )}
                        fullWidth
                        multiLine
                        label={TranslateW("installView.notes")}
                        value={formState.repairNotes}
                        onChange={e => setGlobalState("repairNotes", e.target.value)}
                        error={requiredFieldsMissing.repairNotes}
                        disabled={!formState.workItemTypeId}
                    />
                </Grid>
            )}
            {renderServiceActionField && (
                <Grid item xs={12} md={6}>
                    <Select
                        required
                        label={TranslateW("installView.service-action")}
                        value={formState.serviceActionId}
                        onChange={e => setGlobalState("serviceActionId", e.target.value)}
                        options={dropdownOptions.serviceActions}
                        error={requiredFieldsMissing.serviceActionId}
                        disabled={!formState.workItemTypeId}
                    />
                </Grid>
            )}
            {renderR3Filed && (
                <Grid item xs={dynamicWidth} md={dynamicWidth} lg={dynamicWidth}>
                    <OnOffSwitch
                        label={TranslateW("R3Filed")}
                        labelPlacement="start"
                        value={formState.r3Filed}
                        onChange={e => setGlobalState("r3Filed", e.target.checked)}
                        disabled={!formState.workItemTypeId}
                    />
                </Grid>
            )}
            {renderNewLocation && (
                <Grid item xs={6} md={6} lg={6}>
                    <Select
                        required
                        label={TranslateW("installView.new-location")}
                        value={formState.newLocationId}
                        onChange={e => setGlobalState("newLocationId", e.target.value)}
                        options={dropdownOptions.locations}
                        error={requiredFieldsMissing.newLocationId}
                        disabledMenuItems={[formState.assetLocationId]}
                        menuProps={menuProps}
                    />
                </Grid>
            )}
            {renderAssetDetails && (
                <Grid item xs={12} md={12}>
                    <RecentLeaks
                        leaks={recentLeaksData.leaks}
                        totalCount={recentLeaksData.totalCount}
                        loading={recentLeaksData.loading}
                    />
                </Grid>
            )}
        </Grid>
    );
};

export default EnterService;
